
import React from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Clock, Unlink, AlertTriangle, PanelTop, Layers, Anchor } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EstimateStratigraphy } from '@/hooks/useEstimateStratigraphies';
import MiniSectionPreview from '@/components/configurator-v2/list-view/MiniSectionPreview';
import { normalizeSnapshotLayers } from '@/components/configurator-v2/hooks/normalizeSnapshotLayers';
import EstimateStratigraphyActions from './EstimateStratigraphyActions';
import RowStaleDot from '../RowStaleDot';
import { COMMON_VAT_RATES } from '@/utils/pricing/computeVatBreakdown';
import { useOrgProfile, PRICING_DEFAULTS } from '@/hooks/useOrgSettings';
// Formato italiano per i valori a schermo. NON usarlo nei value degli <input>
// (area, difficoltà, costo unitario orfano): il parse vuole il punto decimale.
import { formatNumber, formatEuro } from '@/utils/format';
import type { RowRevenue } from '@/utils/pricing/distributeSellingPrice';
import type { EstimateRowCost } from '@/utils/pricing/computeEstimateCostBase';

interface EstimateStratigraphyRowProps {
  item: EstimateStratigraphy & { stratigraphy?: any };
  editingId: string | null;
  editData: any;
  canEdit: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isUpdatingPrices?: boolean;
  onStartEdit: (stratigraphy: EstimateStratigraphy) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: (id: string) => void;
  onUpdatePrices?: (estimateStratigraphyId: string, originalStratigraphyId: string) => void;
  onEditDataChange: (data: any) => void;
  /** Apre il dialog di ricollegamento al catalogo per row orfane. */
  onReconnect?: (item: EstimateStratigraphy & { stratigraphy?: any }) => void;
  /** Apre il viewer read-only dello snapshot per row orfane (chiamato dall'icona Eye). */
  onViewSnapshot?: (item: EstimateStratigraphy & { stratigraphy?: any }) => void;
  /** Tariffa oraria (globale) per scomporre costo manodopera/materiali. */
  costPerHour?: number;
  /** Costi scomposti di questa riga: sfrido e smaltimento sono colonne fisse. */
  rowCost?: EstimateRowCost;
  /** Ricavo gia` ripartito per questa riga. Assente = colonne nascoste. */
  revenue?: RowRevenue;
  showRevenue?: boolean;
  /** Codici livello finitura disponibili per l'org (es. ['Q1','Q2','Q3','Q4']). */
  finishLevelCodes?: string[];
  /** Cambia il livello di finitura della riga. null = nessuna finitura. */
  onUpdateFinishLevel?: (id: string, finishCode: string | null) => void;
  /** Cambia l'aliquota IVA per la riga. null = ripristina default org. */
  onUpdateVatRate?: (id: string, rate: number | null) => void;
  /** Cambia il mark-up % override per la riga. null = ripristina markup categoria org. */
  onUpdateMarkupPct?: (id: string, pct: number | null) => void;
  /** Cambia il coefficiente difficoltà per la riga (default 1.00, range 0.80–2.00). */
  onUpdateDifficultyMultiplier?: (id: string, mult: number) => void;
}

const EstimateStratigraphyRow = ({
  item,
  editingId,
  editData,
  canEdit,
  isUpdating,
  isDeleting,
  isUpdatingPrices = false,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onUpdatePrices,
  onEditDataChange,
  onReconnect,
  onViewSnapshot,
  costPerHour = 30,
  rowCost,
  revenue,
  showRevenue = false,
  finishLevelCodes,
  onUpdateFinishLevel,
  onUpdateVatRate,
  onUpdateMarkupPct,
  onUpdateDifficultyMultiplier,
}: EstimateStratigraphyRowProps) => {
  const { data: orgProfile } = useOrgProfile();
  const orgVatRate = typeof orgProfile?.iva_pct === 'number'
    ? orgProfile.iva_pct
    : PRICING_DEFAULTS.iva_pct;
  const currentVatRate = item.vatRate ?? null; // null = default org
  const currentMarkupPct = item.markupPct ?? null; // null = markup categoria org
  // Coefficiente difficoltà: stato locale per editing libero (commit onBlur).
  // Default 1.00 quando NULL/undefined dal DB (righe pre-feature).
  const difficultyFromItem = (item as { difficultyMultiplier?: number }).difficultyMultiplier ?? 1;
  // Scomposizione costi riga. labor/m² = (minuti base + minuti finitura)/60 × tariffa.
  // La difficolta` moltiplica SOLO la manodopera; i materiali (tutto il resto) no.
  //   costo_manodopera = area × labor/m² × difficolta
  //   costo_materiale  = area × (costo_unitario − labor/m²)
  //   (manodopera + materiale = totale)
  const baseInstallMin = Number(
    (item.stratigraphyData as { installation_time_per_sqm?: number } | undefined)
      ?.installation_time_per_sqm ?? 0,
  );
  const finishLaborMin = Number(
    (item as { finishLaborMinutesPerSqm?: number | null }).finishLaborMinutesPerSqm ?? 0,
  );
  const laborCostPerSqm = ((baseInstallMin + finishLaborMin) / 60) * costPerHour;
  // Le quattro voci di costo vengono dalla STESSA funzione che alimenta il
  // prezzo di vendita, cosi` l'uguaglianza
  //   Manodopera + Materiale + Sfrido + Smaltimento = Totale
  // vale per costruzione e non per coincidenza. Il ripiego (righe senza costo
  // scomposto) e` il vecchio calcolo per differenza.
  const rowLaborCost = rowCost
    ? rowCost.labor
    : item.area * laborCostPerSqm * difficultyFromItem;
  const rowMaterialCost = rowCost
    // `materials` include gia` perimetro, sfrido e smaltimento: qui si mostra la
    // sola parte "materiali e viti", perche' gli altri hanno colonne proprie.
    ? Math.max(0, rowCost.materials - rowCost.waste - rowCost.disposal)
    : Math.max(0, item.area * (item.unitCost - laborCostPerSqm));
  // Costo unitario EFFETTIVO: il €/m² che moltiplicato per la superficie da`
  // il Totale. Si ricava dal totale, non ricalcolando la maggiorazione, cosi`
  // l'aritmetica a video torna sempre — anche sulle righe il cui totale fu
  // scritto con parametri diversi da quelli di oggi.
  // Totale della riga: quando il costo scomposto c'e`, si usa quello. Cosi` le
  // righe non ancora ricalcolate mostrano SUBITO il costo su cui e` calcolato il
  // prezzo, invece del valore salvato che non ha ancora sfrido e smaltimento.
  const rowTotalCost = rowCost ? rowCost.total : item.totalCost;
  const effectiveUnitCost = item.area > 0 && rowTotalCost > 0
    ? rowTotalCost / item.area
    : item.unitCost;
  const [diffDraft, setDiffDraft] = React.useState<string>(difficultyFromItem.toFixed(2));
  React.useEffect(() => {
    setDiffDraft(difficultyFromItem.toFixed(2));
  }, [difficultyFromItem]);
  const isEditing = editingId === item.id;

  /**
   * Bozza in TESTO della quantità, come già si fa per la difficoltà.
   *
   * Serve perché lo stato di modifica tiene un NUMERO: scrivendo "12," il valore
   * diventava `Number("12.")` = 12, il campo si ridisegnava con "12" e la virgola
   * appena digitata spariva — non si riusciva mai ad arrivare ai decimali.
   * Qui resta il testo così com'è scritto, e a monte si manda il numero.
   */
  const [areaDraft, setAreaDraft] = React.useState<string>('');
  React.useEffect(() => {
    // Si allinea solo ENTRANDO in modifica: farlo a ogni cambio di `editData`
    // riscriverebbe il campo mentre lo si sta digitando.
    if (isEditing) setAreaDraft(item.area ? String(item.area).replace('.', ',') : '');
  }, [isEditing, item.area]);
  // Una row è "orfana" quando ha lo snapshot ma l'originale del catalogo è
  // stata eliminata (FK SET NULL → originalStratigraphyId NULL). Su queste
  // row non possiamo "Aggiorna prezzi" automatico — offriamo edit manuale.
  const isOrphan = !!item.isSnapshot && !item.originalStratigraphyId;

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleUpdatePrices = () => {
    if (onUpdatePrices && item.originalStratigraphyId) {
      console.log('Updating prices for item:', item.id, 'original:', item.originalStratigraphyId);
      onUpdatePrices(item.id, item.originalStratigraphyId);
    } else {
      console.warn('Cannot update prices - missing data:', {
        onUpdatePrices: !!onUpdatePrices,
        originalStratigraphyId: item.originalStratigraphyId
      });
    }
  };

  return (
    <TableRow key={item.id} className="hover:bg-gray-50">
      <TableCell>
        <div className="w-16 h-12 border border-gray-200 rounded overflow-hidden bg-white">
          {item.stratigraphy?.layers ? (
            <MiniSectionPreview
              layers={normalizeSnapshotLayers(item.stratigraphy.layers as unknown[])}
              className="w-full h-full"
              layerType={item.stratigraphy?.type === 'ceiling' ? 'ceiling' : 'wall'}
            />
          ) : (
            <div className="w-full h-full bg-gray-50 flex items-center justify-center">
              <span className="text-xs text-gray-400">N/A</span>
            </div>
          )}
        </div>
      </TableCell>
      
      <TableCell>
        {isEditing ? (
          <Input
            value={editData.name || ''}
            onChange={(e) => onEditDataChange({ ...editData, name: e.target.value })}
            className="min-w-48"
          />
        ) : (
          <div>
            <div className="font-medium text-gray-900">{item.name}</div>
            {item.description && (
              <div className="text-sm text-muted-foreground mt-1">{item.description}</div>
            )}
            {item.isSnapshot && !isOrphan && (
              <Badge variant="outline" className="text-xs mt-1 bg-blue-50 text-blue-700 border-blue-200">
                Copia indipendente
              </Badge>
            )}
            {isOrphan && (
              <Badge variant="outline" className="text-xs mt-1 bg-amber-50 text-amber-800 border-amber-200 gap-1">
                <Unlink className="h-3 w-3" />
                Originale eliminata
              </Badge>
            )}
          </div>
        )}
      </TableCell>
      
      <TableCell>
        <div className="space-y-2">
          <div>
            <div className="font-medium text-sm">{item.stratigraphy?.name || 'Nome non disponibile'}</div>
            {/* F17.2.14: badge type-aware. Ceiling = badge indaco + icona PanelTop. */}
            {item.stratigraphy?.type === 'ceiling' ? (
              <Badge variant="outline" className="text-[10px] mt-0.5 bg-indigo-50 text-indigo-700 border-indigo-200 gap-1">
                <PanelTop className="h-3 w-3" />
                Controsoffitto
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] mt-0.5 bg-slate-50 text-slate-700 border-slate-200 gap-1">
                <Layers className="h-3 w-3" />
                Parete
              </Badge>
            )}
          </div>
          {item.stratigraphy?.total_thickness && (
            <Badge variant="outline" className="text-xs">
              {item.stratigraphy.total_thickness}mm
            </Badge>
          )}
          {item.stratigraphy?.is_certified && (
            <Badge variant="default" className="text-xs bg-green-100 text-green-800">
              Certificata
            </Badge>
          )}
          {/* F17.2.14: per i ceiling mostra mini-metadata perimetro */}
          {item.stratigraphy?.type === 'ceiling' && item.perimeterM != null && item.perimeterM > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Anchor className="h-2.5 w-2.5" />
              Perimetro {formatNumber(item.perimeterM, 1)}m
              {item.perimeterProfileCost != null && item.perimeterProfileCost > 0 && (
                <span className="font-mono">· {formatEuro(item.perimeterProfileCost)}</span>
              )}
            </div>
          )}
        </div>
      </TableCell>
      
      <TableCell>
        {isEditing ? (
          <Input
            type="text"
            inputMode="decimal"
            value={areaDraft}
            onChange={(e) => {
              const raw = e.target.value;
              // Solo cifre e UN separatore decimale, virgola o punto: così si
              // può digitare "12," e poi i decimali senza che il campo si
              // riscriva da solo.
              if (raw !== '' && !/^\d*[.,]?\d*$/.test(raw)) return;
              setAreaDraft(raw);
              const n = Number(raw.replace(',', '.'));
              onEditDataChange({
                ...editData,
                area: raw === '' || !Number.isFinite(n) ? 0 : n,
              });
            }}
            className="w-24"
            placeholder="0,00"
          />
        ) : (
          <div className="font-mono text-sm">
            {formatNumber(item.area)}
          </div>
        )}
      </TableCell>

      {/* Colonna "Altezza (m)" RIMOSSA dalla tabella su richiesta: non incide sul
          costo del preventivo. L'altezza parete serve solo al conteggio pezzi lastre
          nel riepilogo materiali (useMaterialsSummary), dove usa il valore impostato
          all'aggiunta della stratigrafia o il default 2.70m. Ripristinabile da git
          (insieme all'intestazione in EstimateStratigraphiesTable). */}

      {/* Livello di finitura (Q1-Q4) editabile inline. Cambiarlo ricalcola
          costo finitura + manodopera e aggiorna unit/total cost (handler in
          EstimateManagement). 'Nessuna' azzera il costo finitura. */}
      <TableCell>
        {canEdit && onUpdateFinishLevel ? (
          <Select
            value={(item as { finishLevel?: string | null }).finishLevel ?? '__none__'}
            onValueChange={(v) => onUpdateFinishLevel(item.id, v === '__none__' ? null : v)}
            disabled={isUpdating}
          >
            <SelectTrigger className="h-8 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                <span className="text-muted-foreground">Nessuna</span>
              </SelectItem>
              {/* Solo i livelli realmente configurati per l'org (niente fallback
                  fittizio: mostrare Q1-Q4 inesistenti darebbe 0€/0min e confonde). */}
              {(finishLevelCodes ?? []).map((code) => (
                <SelectItem key={code} value={code}>{code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (item as { finishLevel?: string | null }).finishLevel ? (
          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">
            {(item as { finishLevel?: string | null }).finishLevel}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>

      <TableCell>
        {isEditing && isOrphan ? (
          <div className="space-y-1">
            <Input
              type="number"
              value={editData.unitCost ?? ''}
              onChange={(e) => onEditDataChange({ ...editData, unitCost: Number(e.target.value) })}
              className="w-24 font-mono"
              min="0"
              step="0.01"
            />
            <div className="text-[10px] text-amber-700 leading-tight">
              Modifica manuale (originale eliminata)
            </div>
          </div>
        ) : (
          <>
            {/* COSTO UNITARIO EFFETTIVO. `unit_cost` NON contiene la
                maggiorazione per difficolta`, mentre Manodopera, Tempo, Totale
                e Ricavo €/m² la contengono tutti: era l'unica colonna su una
                base diversa. Chi moltiplicava questo numero per la superficie
                sbagliava — sulla Controparete 6.5 di 1.662 € — e affiancato al
                Ricavo €/m² faceva leggere un margine piu` alto del vero.
                Ora si mostra il €/m² che moltiplicato per i metri da` il
                Totale, col valore base sotto. Con difficolta` 1 (176 righe su
                178) i due coincidono e non cambia nulla. */}
            <div className="font-mono text-sm whitespace-nowrap">
              {formatEuro(effectiveUnitCost)}
            </div>
            {difficultyFromItem !== 1 && (
              <div className="text-[10px] text-muted-foreground font-mono leading-tight mt-0.5">
                base {formatEuro(item.unitCost)}
              </div>
            )}
            {isEditing && !isOrphan && (
              <div className="text-xs text-muted-foreground mt-1">
                Costo fisso dal calcolo
              </div>
            )}
          </>
        )}
      </TableCell>

      {/* Coefficiente difficoltà: moltiplicatore costo per-riga (default 1.00).
          Editing libero con virgola/punto, commit onBlur. Range 0.80–2.00 imposto
          dal CHECK constraint DB; il client clampa nello stesso range prima del save. */}
      <TableCell>
        {canEdit && onUpdateDifficultyMultiplier ? (
          <div className="space-y-0.5">
            <Input
              type="text"
              inputMode="decimal"
              value={diffDraft}
              onChange={(e) => setDiffDraft(e.target.value)}
              onBlur={() => {
                const raw = diffDraft.replace(',', '.').trim();
                const n = Number(raw);
                if (!isFinite(n) || isNaN(n)) {
                  setDiffDraft(difficultyFromItem.toFixed(2));
                  return;
                }
                const clamped = Math.min(2.0, Math.max(0.8, n));
                if (Math.abs(clamped - difficultyFromItem) < 0.001) {
                  setDiffDraft(clamped.toFixed(2));
                  return;
                }
                setDiffDraft(clamped.toFixed(2));
                onUpdateDifficultyMultiplier(item.id, clamped);
              }}
              className="w-20 h-8 font-mono text-sm"
              title="Coefficiente difficoltà (solo MANODOPERA, non materiali) — Range 0.80–2.00. Esempi: 1.15 alta, 1.25 molto alta, 1.30 accesso difficile, 1.50 impalcato esterno."
            />
            <div className={`text-[10px] leading-tight ${difficultyFromItem === 1 ? 'text-muted-foreground' : 'text-amber-700 font-medium'}`}>
              {difficultyFromItem === 1
                ? 'neutro'
                : `${difficultyFromItem > 1 ? '+' : ''}${formatNumber((difficultyFromItem - 1) * 100, 0)}%`}
            </div>
          </div>
        ) : (
          <div className="font-mono text-sm">{formatNumber(difficultyFromItem)}</div>
        )}
      </TableCell>

      {/* Costo Manodopera (base + finitura, × difficolta) */}
      <TableCell>
        <div className="font-mono text-sm text-amber-800 whitespace-nowrap">{formatEuro(rowLaborCost)}</div>
        {difficultyFromItem !== 1 && (
          <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
            incl. difficoltà
          </div>
        )}
      </TableCell>
      {/* Costo Materiale (lastre/isolanti + viti + materiali finitura) */}
      {/* SFRIDO e SMALTIMENTO: costi diretti che prima non comparivano da
          nessuna parte. Lo sfrido era contato dal Riepilogo Materiali ma non
          dal preventivo; lo smaltimento non era contato da nessuno. Gli stessi
          numeri finiscono nella Vista vendita, quindi le due letture tornano. */}
      <TableCell>
        <div className="font-mono text-sm text-amber-800 whitespace-nowrap">
          {rowCost ? formatEuro(rowCost.waste) : '—'}
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-sm text-amber-800 whitespace-nowrap">
          {rowCost ? formatEuro(rowCost.disposal) : '—'}
        </div>
      </TableCell>

      <TableCell>
        <div className="font-mono text-sm text-slate-700 whitespace-nowrap">{formatEuro(rowMaterialCost)}</div>
      </TableCell>

      <TableCell>
        <div className="font-semibold text-primary whitespace-nowrap">
          {formatEuro(rowTotalCost)}
        </div>
        {difficultyFromItem !== 1 && (
          <div className="text-[10px] text-muted-foreground font-mono leading-tight mt-0.5">
            base {formatEuro(item.unitCost * item.area)}
          </div>
        )}
      </TableCell>

      {/* RICAVI PER RIGA. Il prezzo di vendita e` calcolato una volta sul
          preventivo intero e qui solo ripartito: la somma della colonna
          "Ricavo & Margine" e` esattamente il prezzo lordo dell'offerta. */}
      {showRevenue && (
        <>
          <TableCell className="bg-emerald-50/40">
            <div className="font-mono text-sm text-emerald-800 whitespace-nowrap">
              {revenue ? formatEuro(revenue.revenueLabor) : '—'}
            </div>
          </TableCell>
          <TableCell className="bg-emerald-50/40">
            <div className="font-mono text-sm text-emerald-800 whitespace-nowrap">
              {revenue ? formatEuro(revenue.revenueMaterials) : '—'}
            </div>
            {revenue && revenue.revenueFinish > 0 && (
              <div className="text-[10px] text-muted-foreground font-mono leading-tight mt-0.5">
                finitura {formatEuro(revenue.revenueFinish)}
              </div>
            )}
          </TableCell>
          <TableCell className="bg-emerald-50/40">
            <div className="font-mono text-sm text-emerald-800 whitespace-nowrap">
              {revenue ? formatEuro(revenue.revenuePerSqm) + '/m²' : '—'}
            </div>
          </TableCell>
          <TableCell className="bg-emerald-50/40">
            <div className="font-semibold text-emerald-900 whitespace-nowrap">
              {revenue ? formatEuro(revenue.revenueTotal) : '—'}
            </div>
            {revenue && (
              <div className={revenue.margin >= 0
                ? 'text-[10px] font-mono leading-tight mt-0.5 text-emerald-700'
                : 'text-[10px] font-mono leading-tight mt-0.5 text-red-600'}>
                {revenue.margin >= 0 ? '+' : ''}{formatEuro(revenue.margin)} ({revenue.marginPct.toFixed(1)}%)
              </div>
            )}
          </TableCell>
        </>
      )}

      {/* Tempo Totale manodopera = (tempo base stratigrafia + tempo finitura)
          × superficie × difficoltà. Sola lettura: si aggiorna al variare della
          finitura (finishLaborMinutesPerSqm) e della difficoltà. */}
      <TableCell>
        {(() => {
          const perSqm = (baseInstallMin + finishLaborMin) * difficultyFromItem;
          const totalMin = perSqm * (item.area || 0);
          const fmt = (min: number) => {
            const t = Math.round(min);
            const h = Math.floor(t / 60);
            const m = t % 60;
            return h > 0 ? `${h}h ${m}m` : `${m}m`;
          };
          if (baseInstallMin + finishLaborMin <= 0) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <div>
              <div className="font-mono text-sm">{fmt(totalMin)}</div>
              <div className="text-[10px] text-muted-foreground font-mono leading-tight mt-0.5">
                {formatNumber(perSqm, 1)} min/m²
              </div>
            </div>
          );
        })()}
      </TableCell>

      {/* Celle Margine (mark-up) e IVA NASCOSTE su richiesta (non utilizzate).
          I default org restano validi nel calcolo prezzo di vendita. Per
          riattivarle: togliere questo commento (e quello nell'header della
          tabella) ripristinando le due <TableCell> qui sotto.

      <TableCell>
        {canEdit && onUpdateMarkupPct ? (
          <Select
            value={currentMarkupPct == null ? '__default__' : String(currentMarkupPct)}
            onValueChange={(v) => {
              const pct = v === '__default__' ? null : Number(v);
              onUpdateMarkupPct(item.id, pct);
            }}
          >
            <SelectTrigger className="h-8 w-[104px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">
                <span className="text-muted-foreground">Default</span>
              </SelectItem>
              {[10, 20, 25, 30, 40, 50, 70, 100].map(p => (
                <SelectItem key={p} value={String(p)}>+{p}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="text-xs text-muted-foreground">
            {currentMarkupPct != null ? `+${currentMarkupPct}%` : 'Default'}
          </div>
        )}
      </TableCell>

      <TableCell>
        {canEdit && onUpdateVatRate ? (
          <Select
            value={currentVatRate == null ? '__default__' : String(currentVatRate)}
            onValueChange={(v) => {
              const rate = v === '__default__' ? null : Number(v);
              onUpdateVatRate(item.id, rate);
            }}
          >
            <SelectTrigger className="h-8 w-[88px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">
                <span className="text-muted-foreground">Default ({orgVatRate}%)</span>
              </SelectItem>
              {COMMON_VAT_RATES.map(r => (
                <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="text-xs text-muted-foreground">
            {currentVatRate != null ? `${currentVatRate}%` : `${orgVatRate}%`}
          </div>
        )}
      </TableCell>
      */}

      <TableCell>
        <div className="text-xs">
          {item.isSnapshot ? (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Aggiornati al:</span>
              </div>
              <div className="font-medium">{formatDate(item.pricesUpdatedAt)}</div>
              <RowStaleDot estimateStratigraphyId={item.id} skip={isOrphan} />
              {/* Aggiorna Prezzi: solo se l'originale del catalogo esiste ancora. */}
              {!isOrphan && onUpdatePrices && item.originalStratigraphyId && canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUpdatePrices}
                  disabled={isUpdatingPrices}
                  className="mt-1 h-7 px-2 text-xs w-full"
                >
                  {isUpdatingPrices ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Aggiornando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Aggiorna Prezzi
                    </>
                  )}
                </Button>
              )}
              {/* Row orfana: aggiornamento auto non possibile, suggerisce
                  alternative. */}
              {isOrphan && canEdit && (
                <div className="space-y-1.5 mt-1">
                  <div className="flex items-start gap-1 text-amber-800 leading-tight">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span className="text-[11px]">
                      Originale eliminata: modifica il prezzo manualmente o ricollega al catalogo.
                    </span>
                  </div>
                  {onReconnect && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReconnect(item)}
                      className="h-7 px-2 text-xs w-full gap-1"
                    >
                      <Unlink className="h-3 w-3" />
                      Ricollega al catalogo
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">Dati live</span>
          )}
        </div>
      </TableCell>
      
      <TableCell>
        <EstimateStratigraphyActions
          item={item}
          editingId={editingId}
          canEdit={canEdit}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
          onStartEdit={() => onStartEdit(item)}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          onDelete={() => onDelete(item.id)}
          onViewSnapshot={onViewSnapshot ? () => onViewSnapshot(item) : undefined}
        />
      </TableCell>
    </TableRow>
  );
};

export default EstimateStratigraphyRow;
